/**
 * Serves both a root comment and a reply: a reply is the same card arriving
 * without the threading props, so nothing here branches on which it is.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useDeleteTrackerCommentMutation,
  useUpdateTrackerCommentMutation,
  type TrackerCommentResponseDto,
} from '@/entities/tracker-comment';
import { Comment } from '@/entities/project-comment';
import { useAuth, useUserDisplay } from '@/entities/user';
import { notificationService } from '@/lib/services/notification.service';

import { formatCommentTimestamp } from '../../lib/commentThreads';
import { useCanEditTracker } from '../../lib/tracker-access';

export interface TrackerCommentCardProps {
  comment: TrackerCommentResponseDto;
  trackerId: string;
  rowKey: string;
  /** A `?comment=<id>` deeplink target: scrolls itself into view and flashes a ring. */
  isFocused: boolean;
  /** Reveals the reply composer and the thread underneath the card. */
  selected?: boolean;
  /** Adds the settle/reopen control. Settling is open to anyone, not just the author. */
  resolvable?: boolean;
  /** Rendered under the body — hosts the "N replies" expander. */
  footerSlot?: ReactNode;
  onReply?: (content: Record<string, unknown>) => Promise<void>;
  /** The thread's replies, rendered inside the same card box. */
  children?: ReactNode;
}

export function TrackerCommentCard({
  comment,
  trackerId,
  rowKey,
  isFocused,
  selected = false,
  resolvable = false,
  footerSlot,
  onReply,
  children,
}: TrackerCommentCardProps) {
  const { t } = useTranslation('trackers');

  const authorDisplay = useUserDisplay(comment.createdBy);
  const currentUserId = useAuth((s) => s.signedInUser.value)?.id ?? null;
  const updateMutation = useUpdateTrackerCommentMutation({
    trackerId,
    rowKey,
    commentId: comment.id,
  });
  const deleteMutation = useDeleteTrackerCommentMutation({
    trackerId,
    rowKey,
    commentId: comment.id,
  });

  const ref = useRef<HTMLDivElement | null>(null);
  const [highlight, setHighlight] = useState(isFocused);
  useEffect(() => {
    if (!isFocused) return;
    ref.current?.scrollIntoView({ block: 'center' });
    setHighlight(true);
    const timer = setTimeout(() => setHighlight(false), 2000);
    return () => clearTimeout(timer);
  }, [isFocused]);

  const canEdit = useCanEditTracker();
  const isMine = canEdit && currentUserId !== null && comment.createdBy === currentUserId;
  // A rule signs its comments with its author's id, but the words are the rule's.
  const byRule = Boolean(comment.automationId);
  const authorName = byRule
    ? comment.automationName
      ? t('trackersBoard.trackerCommentsPanel.automationAuthor', { name: comment.automationName })
      : t('trackersBoard.trackerCommentsPanel.automationAuthorUnnamed')
    : (authorDisplay.name ?? t('trackersBoard.trackerCommentsPanel.unknownUser'));

  return (
    <div
      ref={ref}
      className={
        'rounded-lg transition-shadow ' +
        (highlight ? 'ring-2 ring-primary ring-offset-2 ring-offset-card' : '')
      }
    >
      <Comment
        authorName={authorName}
        authorAvatarUrl={byRule ? undefined : (authorDisplay.avatarUrl ?? undefined)}
        timestamp={formatCommentTimestamp(comment.createdAt)}
        content={comment.content}
        resolved={comment.resolved}
        selected={selected}
        footerSlot={footerSlot}
        onReply={canEdit ? onReply : undefined}
        resolveLabel={
          comment.resolved
            ? t('trackersBoard.trackerCommentsPanel.reopenComment')
            : t('trackersBoard.trackerCommentsPanel.resolveComment')
        }
        onResolve={
          resolvable && canEdit
            ? () => {
                updateMutation.mutate(
                  { resolved: !comment.resolved },
                  {
                    onError: () =>
                      notificationService.showError(
                        comment.resolved
                          ? t('trackersBoard.trackerCommentsPanel.couldNotReopenThe')
                          : t('trackersBoard.trackerCommentsPanel.couldNotResolveThe'),
                      ),
                  },
                );
              }
            : undefined
        }
        onCopyLink={() => {
          const url = new URL(window.location.href);

          url.searchParams.set('item', rowKey);

          url.searchParams.set('comment', comment.id);
          void navigator.clipboard.writeText(url.toString()).then(
            () =>
              notificationService.showSuccess(
                t('trackersBoard.trackerCommentsPanel.commentLinkCopied'),
              ),
            () =>
              notificationService.showError(
                t('trackersBoard.trackerCommentsPanel.couldNotCopyTheLink'),
              ),
          );
        }}
        onEdit={
          isMine && !byRule
            ? async (nextContent) => {
                await updateMutation.mutateAsync({ content: nextContent });
              }
            : undefined
        }
        onDelete={
          isMine
            ? () => {
                deleteMutation.mutate(undefined, {
                  onError: () =>
                    notificationService.showError(
                      t('trackersBoard.trackerCommentsPanel.couldNotDeleteThe'),
                    ),
                });
              }
            : undefined
        }
      >
        {children}
      </Comment>
    </div>
  );
}
